import { ApolloClient } from '@apollo/client';
import { ReactElement } from 'react';
import { assign, createMachine } from 'xstate';
import { ValidatingInputProps } from './validating-input.component';

export const validatorMachine = createMachine(
  {
    id: 'validatorMachine',
    tsTypes: {} as import('./validating-input.machine.typegen').Typegen0,
    predictableActionArguments: true,
    schema: {
      context: {} as ValidatorContext,
      events: {} as ValidatorEvents,
      services: {} as ValidatorServices,
    },
    context: {
      apollo: undefined,
      fieldName: '',
      message: '',
      onChange: () => {},
      optional: false,
      validate: async () => true, // eslint-disable-line require-await
      value: '',
    },
    initial: 'validating',
    states: {
      valid: {
        id: 'valid',
        entry: 'reportChangeAsValid',
        on: {
          CHANGE: { target: 'validating', actions: 'updateValue' },
        },
      },
      invalid: {
        id: 'invalid',
        entry: 'reportChangeAsInvalid',
        on: {
          CHANGE: { target: 'validating', actions: 'updateValue' },
        },
        initial: 'hiding',
        states: {
          hiding: {
            after: {
              // don't need to show if input is empty (will show onBlur)
              debounceMessage: { cond: 'hasValue', target: 'showing' },
            },
            on: {
              BLUR: 'showing',
            },
          },
          showing: {
            on: {
              FOCUS: 'hiding',
            },
          },
        },
      },
      validating: {
        id: 'validating',
        entry: ['clearMessage', 'reportChangeAsInvalid'],
        initial: 'waiting',
        on: {
          // re-enters validating state and discards any pending validator service response
          CHANGE: { target: '#validating', actions: 'updateValue' },
        },
        states: {
          waiting: {
            after: {
              debounceValidation: 'checking',
            },
          },
          checking: {
            invoke: {
              id: 'validator',
              src: 'validator',
              onDone: [
                { cond: 'validationSuccess', target: '#valid' },
                { target: '#invalid', actions: 'setMessage' },
              ],
              // if the validate function unexpectedly throws an error
              onError: { target: '#invalid', actions: 'setGenericMessage' },
            },
          },
        },
      },
    },
  },
  {
    actions: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      clearMessage: assign(_context => ({
        message: '',
      })),
      reportChangeAsValid: context => {
        context.onChange(context.value, {
          name: context.fieldName,
          isValid: true,
          optional: context.optional,
        });
      },
      reportChangeAsInvalid: context => {
        context.onChange(context.value, {
          name: context.fieldName,
          isValid: false,
          optional: context.optional,
        });
      },
      setMessage: assign((_context, event) => ({
        message: event.data.message,
      })),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setGenericMessage: assign(_context => ({
        message: "Oops that didn't work",
      })),
      updateValue: assign((_context, event) => ({
        value: event.value,
      })),
    },
    delays: {
      debounceMessage: 1,
      debounceValidation: 1,
    },
    guards: {
      hasValue: context => !!context.value,
      validationSuccess: (_, event) => event.data.valid,
    },
    services: {
      validator: async context => {
        if (context.optional && !context.value) {
          return { valid: true, message: '' };
        }
        const result = await context.validate({ apollo: context.apollo!, value: context.value });
        if (result === true) {
          return { valid: true, message: '' };
        } else {
          return { valid: false, message: result || '' };
        }
      },
    },
  },
);

type ValidatorContext = {
  apollo?: ApolloClient<{}>;
  fieldName: string;
  message: string | ReactElement;
  onChange: (
    value: string | boolean,
    meta: { name: string; isValid: boolean; optional: boolean },
  ) => void;
  optional: boolean;
  validate: ValidatingInputProps['validate'];
  value: string | boolean;
};

type ValidatorEvents =
  | { type: 'CHANGE'; value: string | boolean }
  | { type: 'BLUR' }
  | { type: 'FOCUS' };

type ValidatorServices = {
  validator: {
    data: { valid: boolean; message: string | ReactElement };
  };
};
